import React, { FC, Fragment } from "react"
import GatsbyImage from "gatsby-image"
import styled from "styled-components"

// components
import CustomRichText from "../atoms/CustomRichText"
import CustomPrismicTitle from "../atoms/CustomPrismicTitle"

// styles
import colors from "../../styles/colors"
import mq from "../../styles/breakpoints"
import { fontWeights } from "../../styles/textStyles"
import { fonts } from "../../styles/fonts"

// types
import { ITitleDescriptionImage } from "../../prismic-types/slices/title-description-image"

const TitleDescriptionImage: FC<ITitleDescriptionImage> = ({ primary }) => {
  if (primary.image_position === "left" || primary.image_position === "right")
    return (
      <Container>
        <InnerWrapper>
          <ImageWrapper mirrored={primary.image_position === "left"}>
            <GatsbyImage
              alt={primary.image.alt || ""}
              fluid={primary.image.fluid}
            />
          </ImageWrapper>
          <TextWrapper>
            <TitleWrapper>
              {primary.subtitle && (
                <Label textAlignment="left">{primary.subtitle}</Label>
              )}
              {primary.title.length > 0 && (
                <CustomPrismicTitle data={primary.title} />
              )}
            </TitleWrapper>
            {primary.description1.length > 0 && (
              <CustomRichText data={primary.description1} />
            )}
          </TextWrapper>
        </InnerWrapper>
      </Container>
    )

  return (
    <Fragment>
      {primary.title.length ? (
        <ContentWrapper
          textAlignment={primary.align}
          padding={primary.align === "center"}
        >
          {primary.subtitle ? (
            <Label textAlignment={primary.align}>{primary.subtitle}</Label>
          ) : null}
          {primary.title.length ? (
            <CustomPrismicTitle data={primary.title} />
          ) : null}
          {primary.description1.length ? (
            <CustomRichText data={primary.description1} />
          ) : null}
        </ContentWrapper>
      ) : null}
      {primary?.image?.fluid ? (
        <SingleImageWrapper>
          <GatsbyImage
            alt={primary.image.alt || ""}
            fluid={primary.image.fluid}
          />
        </SingleImageWrapper>
      ) : null}
    </Fragment>
  )
}

export default TitleDescriptionImage

const Container = styled.section`
  position: relative;
  color: ${colors.text};
`

const ImageWrapper = styled.div<{ mirrored?: boolean }>`
  width: 100%;
  height: 100%;
  order: 0;

  img {
    margin-bottom: 0;
    border-radius: 12px;
  }

  ${mq.from.M`
    width: calc(50% - 16px);
    margin-bottom: 0;
    order: ${({ mirrored }: { mirrored?: boolean }) => (mirrored ? 0 : 2)};
  `}
`

const SingleImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  order: 0;

  img {
    margin-bottom: 0;
    border-radius: 12px;
  }
`

const InnerWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 64px;

  ${mq.from.M`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  `}
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 4px;
`
const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  order: 1;
  text-align: left;
  gap: 30px;

  ${mq.from.M`
    width: calc(50% - 16px);
  `}
`

const ContentWrapper = styled.div<{
  padding?: boolean
  textAlignment: "left" | "center" | "right"
}>`
  display: flex;
  justify-content: ${({ textAlignment }) =>
    textAlignment === "center"
      ? "center"
      : textAlignment === "left"
      ? "flex-start"
      : "flex-end"};
  align-items: ${({ textAlignment }) =>
    textAlignment === "center"
      ? "center"
      : textAlignment === "left"
      ? "flex-start"
      : "flex-end"};
  text-align: ${({ textAlignment }) => textAlignment};
  flex-direction: column;
  gap: 4px;

  ${mq.from.M`
  padding: ${({ padding }: { padding: boolean }) =>
    padding ? "0px 128px 0px 128px" : "0px"};
  `}
`

const Label = styled.h5<{ textAlignment: "left" | "center" | "right" }>`
  font-family: ${fonts.primary};
  font-size: 22px;
  line-height: 30px;
  letter-spacing: 0%;
  font-weight: ${fontWeights.medium};
  color: ${colors.purpleLight};
  margin-bottom: 0;
  text-align: ${({ textAlignment }) => textAlignment};
`
